import React from "react";
import { useTranslation } from "react-i18next";
import Button from "../TailwindComponents/Button";
import {
  PiCreditCardLight,
  PiCalendarBlankLight,
  PiReceiptLight,
} from "react-icons/pi";
import { OrgLicenseAccess, LicenseStatus, PaymentMethod } from "../../../store/actions/PlanActions";

interface BillingCardProps {
  access: OrgLicenseAccess | null;
  status: LicenseStatus | null;
  onManageBilling: () => void;
  onViewInvoices?: () => void;
  isPortalLoading?: boolean;
  paymentMethods?: PaymentMethod[];
}

const brandNames: Record<string, string> = {
  visa: "Visa",
  mastercard: "Mastercard",
  amex: "Amex",
  discover: "Discover",
  diners: "Diners",
  jcb: "JCB",
  unionpay: "UnionPay",
};

const BillingCard: React.FC<BillingCardProps> = ({
  access,
  status,
  onManageBilling,
  onViewInvoices,
  isPortalLoading,
  paymentMethods = [],
}) => {
  const { t } = useTranslation();

  const licenseInfo = access?.license_info;
  const hasSubscription = status?.has_active_license && !status.is_trial;
  const hasBilling = paymentMethods.length > 0;

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "—";
    return new Date(dateString).toLocaleDateString(undefined, {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const defaultPm = paymentMethods.find((pm) => pm.is_default) || paymentMethods[0];

  return (
    <div className="rounded-md border border-hairline-soft bg-surface-sunken/70 overflow-hidden flex flex-col">
      {/* Header */}
      <div className="p-5 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <PiCreditCardLight className="h-5 w-5 text-fg-strong flex-shrink-0" />
          <div>
            <h4 className="font-medium text-fg-strong text-sm">
              {t("Billing")}
            </h4>
            <p className="text-xs text-fg-muted mt-0.5">
              {hasBilling || hasSubscription
                ? t("Payment method & invoices")
                : t("No billing information on file")}
            </p>
          </div>
        </div>
        <Button
          variant="outline"
          size="small"
          onClick={onManageBilling}
          isLoading={isPortalLoading}
          icon={<PiCreditCardLight />}
        >
          {hasBilling || hasSubscription ? t("Update Billing") : t("Set Up Billing")}
        </Button>
      </div>

      {/* Details - grid layout for card + invoices on same row */}
      <div className="border-t border-hairline-soft px-5 py-4 flex-1">
        <div className={`grid ${hasSubscription || hasBilling ? "grid-cols-1 sm:grid-cols-3" : "grid-cols-1"} gap-4`}>
          {/* Payment method - show only the default card */}
          <div className="flex items-center gap-3">
            <PiCreditCardLight className="h-4 w-4 text-fg-strong flex-shrink-0" />
            <div>
              <p className="text-xs text-fg-muted mb-0.5">{t("Payment Method")}</p>
              {defaultPm ? (
                <p className="text-sm font-medium text-fg-strong">
                  {brandNames[defaultPm.brand] || defaultPm.brand.charAt(0).toUpperCase() + defaultPm.brand.slice(1)}{" "}
                  •••• {defaultPm.last4}
                </p>
              ) : (
                <p className="text-sm font-medium text-fg-strong">
                  {t("Not configured")}
                </p>
              )}
              {defaultPm && (
                <p className="text-xs text-fg-muted">
                  {t("Expires")} {defaultPm.exp_month.toString().padStart(2, "0")}/{defaultPm.exp_year}
                </p>
              )}
            </div>
          </div>

          {/* Next payment */}
          {hasSubscription && (status?.expires_at || licenseInfo?.expires_at) && (
            <div className="flex items-center gap-3">
              <PiCalendarBlankLight className="h-4 w-4 text-fg-strong flex-shrink-0" />
              <div>
                <p className="text-xs text-fg-muted mb-0.5">{t("Next Payment")}</p>
                <p className="text-sm font-medium text-fg-strong">
                  {formatDate(status?.expires_at || licenseInfo?.expires_at || null)}
                </p>
              </div>
            </div>
          )}

          {/* Invoices - inline on same row */}
          {(hasSubscription || hasBilling) && (
            <button
              onClick={onViewInvoices}
              className="flex items-center gap-3 text-start group hover:bg-surface-sunken/70 rounded transition-colors -mx-1 px-1 py-0.5"
            >
              <PiReceiptLight className="h-4 w-4 text-fg-strong flex-shrink-0" />
              <div>
                <p className="text-xs text-fg-muted mb-0.5">{t("Invoices")}</p>
                <p className="text-sm font-medium text-fg-strong group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
                  {t("View invoices")} →
                </p>
              </div>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default BillingCard;
